import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const { user, login, loginMfa } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captchaText, setCaptchaText] = useState("");
  const [captcha, setCaptcha] = useState({ id: "", image: "" });
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("password");
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");

  // Lock page scroll while the login screen is mounted.
  useEffect(() => {
    document.documentElement.classList.add("public-no-bounce");
    document.body.classList.add("public-no-bounce");
    return () => {
      document.documentElement.classList.remove("public-no-bounce");
      document.body.classList.remove("public-no-bounce");
    };
  }, []);

  const fetchCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaText("");
    try {
      const { data } = await api.post("/auth/captcha");
      setCaptcha({ id: data.captcha_id, image: data.image });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password, {
      captcha_id: captcha.id,
      captcha_text: captchaText,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error || "Login failed");
      // Always rotate captcha after a failed attempt (it's single-use anyway).
      fetchCaptcha();
      return;
    }
    if (res.requires_mfa) {
      setMfaToken(res.mfa_token);
      setStage("mfa");
      setCode("");
    }
  };

  const onSubmitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await loginMfa(mfaToken, code);
    setBusy(false);
    if (!res.ok) setError(res.error || "Code rejected");
  };

  const canSubmit =
    email.trim().length > 0 &&
    password.length > 0 &&
    captcha.id &&
    captchaText.trim().length >= 4 &&
    !busy &&
    !captchaLoading;

  return (
    <div className="fixed inset-0 w-full h-full flex items-center justify-center bg-white px-6 overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm"
      >
        <AnimatePresence mode="wait">
          {stage === "password" ? (
            <motion.div
              key="pw"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              <h1
                className="text-3xl font-black tracking-tighter text-zinc-900 text-center mb-8"
                style={{ fontFamily: "var(--font-cabinet), Cabinet Grotesk, sans-serif" }}
                data-testid="login-heading"
              >
                Sign In
              </h1>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="sr-only">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-zinc-900"
                    data-testid="login-email-input"
                    placeholder="Email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="sr-only">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-zinc-900"
                    data-testid="login-password-input"
                    placeholder="Password"
                  />
                </div>

                {/* CAPTCHA */}
                <div className="space-y-1.5" data-testid="login-captcha-block">
                  <div className="flex items-stretch gap-2">
                    <div className="flex-1 h-20 rounded-md border border-zinc-300 bg-zinc-50 overflow-hidden flex items-center justify-center">
                      {captcha.image ? (
                        <img
                          src={captcha.image}
                          alt="Captcha"
                          className="h-full w-auto select-none"
                          draggable={false}
                          data-testid="login-captcha-image"
                        />
                      ) : (
                        <div className="text-zinc-400 text-xs uppercase tracking-[0.2em]">
                          {captchaLoading ? "Loading…" : "—"}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={fetchCaptcha}
                      disabled={captchaLoading}
                      className="h-20 w-12 rounded-md border-zinc-300 p-0 inline-flex items-center justify-center"
                      data-testid="login-captcha-refresh"
                      aria-label="Refresh captcha"
                    >
                      <RefreshCw className={`w-4 h-4 ${captchaLoading ? "animate-spin" : ""}`} />
                    </Button>
                  </div>
                  <Input
                    type="text"
                    name="captcha-code"
                    inputMode="text"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    required
                    value={captchaText}
                    onChange={(e) =>
                      setCaptchaText(e.target.value.replace(/\s/g, "").toUpperCase())
                    }
                    className="h-12 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-zinc-900 font-mono tracking-[0.3em] text-center uppercase"
                    placeholder="Type the characters"
                    data-testid="login-captcha-input"
                    maxLength={10}
                  />
                </div>

                {error && (
                  <div
                    data-testid="login-error"
                    className="text-sm text-red-600 border border-red-200 bg-red-50 rounded-md px-3 py-2"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full h-12 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-semibold tracking-wide transition-colors"
                  data-testid="login-submit-btn"
                >
                  {busy ? "Signing in…" : "Sign In"}
                </Button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="mfa"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              <h1
                className="text-3xl font-black tracking-tighter text-zinc-900 text-center mb-2"
                style={{ fontFamily: "var(--font-cabinet), Cabinet Grotesk, sans-serif" }}
              >
                Two-factor code
              </h1>
              <p className="text-center text-zinc-600 text-sm mb-8 inline-flex items-center justify-center gap-2 w-full">
                <ShieldCheck className="w-4 h-4" /> Enter the 6-digit code from your authenticator.
              </p>
              <form onSubmit={onSubmitMfa} className="space-y-4" data-testid="login-mfa-form">
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="h-14 rounded-md border-zinc-300 focus-visible:ring-2 focus-visible:ring-zinc-900 font-mono tracking-[0.5em] text-center text-xl"
                  placeholder="000000"
                  data-testid="login-mfa-input"
                />
                {error && (
                  <div
                    data-testid="login-mfa-error"
                    className="text-sm text-red-600 border border-red-200 bg-red-50 rounded-md px-3 py-2"
                  >
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  className="w-full h-12 rounded-md bg-zinc-900 hover:bg-zinc-800 text-white"
                  data-testid="login-mfa-submit-btn"
                >
                  {busy ? "Verifying…" : "Verify & continue"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setStage("password");
                    setCode("");
                    setError("");
                    fetchCaptcha();
                  }}
                  className="w-full h-10 text-zinc-600"
                  data-testid="login-mfa-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
                </Button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
